import { normalizeFilterDateTime } from "./filterDates.js";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function parseInstant(value: string | Date): Date {
  return typeof value === "string" ? new Date(value) : value;
}

function calendarParts(d: Date, timeZone: string) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const year = Number.parseInt(get("year"), 10);
  const monthName = get("month");
  const monthIdx = MO.findIndex((m) => m === monthName.slice(0, 3));
  return {
    weekday: get("weekday").slice(0, 3),
    day: Number.parseInt(get("day"), 10),
    monthIdx: monthIdx >= 0 ? monthIdx : d.getUTCMonth(),
    year,
    hour: Number.parseInt(get("hour"), 10),
    minute: Number.parseInt(get("minute"), 10),
  };
}

/** `Thu 02 Jun 26 14:32` in reporting timezone. */
export function formatEventTimeLocal(value: string | Date, timeZone: string): string {
  const d = parseInstant(value);
  if (Number.isNaN(d.getTime())) return typeof value === "string" ? value : "";
  const p = calendarParts(d, timeZone);
  const yy = pad2(p.year % 100);
  return `${p.weekday} ${pad2(p.day)} ${MO[p.monthIdx]} ${yy} ${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** `Thu 02 Jun 26` in reporting timezone. */
export function formatEventDateLocal(value: string | Date, timeZone: string): string {
  const d = parseInstant(value);
  if (Number.isNaN(d.getTime())) return typeof value === "string" ? value : "";
  const p = calendarParts(d, timeZone);
  const yy = pad2(p.year % 100);
  return `${p.weekday} ${pad2(p.day)} ${MO[p.monthIdx]} ${yy}`;
}

/** `14:32` in reporting timezone. */
export function formatEventTimeLocalHm(value: string | Date, timeZone: string): string {
  const d = parseInstant(value);
  if (Number.isNaN(d.getTime())) return "";
  const p = calendarParts(d, timeZone);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Muted UTC subtext: `Thu 02 Jun 26 13:32 UTC`. */
export function formatUtcSubtext(value: string | Date): string {
  const d = parseInstant(value);
  if (Number.isNaN(d.getTime())) return "";
  const p = calendarParts(d, "UTC");
  const yy = pad2(p.year % 100);
  return `${p.weekday} ${pad2(p.day)} ${MO[p.monthIdx]} ${yy} ${pad2(p.hour)}:${pad2(p.minute)} UTC`;
}

export function formatUtcIso(value: string | Date): string {
  const d = parseInstant(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

/** Local hour 0–23 in reporting timezone. */
export function localHourNow(timeZone: string, at: Date = new Date()): number {
  const p = calendarParts(at, timeZone);
  return p.hour;
}

/**
 * Chart axis label for API `YYYY-MM-DD` reporting-day keys.
 * Example: `2026-06-02` → `Thu 02 Jun 26`.
 */
export function formatDashboardDayLabel(isoDay: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay.trim());
  if (!m) return isoDay;
  const y = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isFinite(y) || month < 0 || month > 11 || day < 1 || day > 31) return isoDay;
  const dt = new Date(Date.UTC(y, month, day, 12, 0, 0));
  const wd = WD[dt.getUTCDay()];
  return `${wd} ${pad2(day)} ${MO[month]} ${pad2(y % 100)}`;
}

/**
 * Compact reporting-day axis label: D/M + weekday initial (letter next to the plot when rotated).
 * Example: `2026-07-31` → `31/7 F`.
 *
 * **Default for short-of-space day axes** on dense multi-day bar charts — use via
 * {@link dailyChartBarAxisProps} / {@link dailyChartRotateAxisLabels}, not the long
 * {@link formatDashboardDayLabel} form.
 */
export function formatDashboardDayLabelCompact(isoDay: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay.trim());
  if (!m) return isoDay;
  const y = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isFinite(y) || month < 0 || month > 11 || day < 1 || day > 31) return isoDay;
  const dt = new Date(Date.UTC(y, month, day, 12, 0, 0));
  const letter = WD[dt.getUTCDay()]![0]!;
  return `${day}/${month + 1} ${letter}`;
}

const WD_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;
const MO_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function dayOrdinal(day: number): string {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return `${day}st`;
  if (j === 2 && k !== 12) return `${day}nd`;
  if (j === 3 && k !== 13) return `${day}rd`;
  return `${day}th`;
}

/**
 * Long prose day for tooltips / criteria.
 * Example: `2026-08-29` → `Thursday 29th August 2026`.
 */
export function formatDashboardDayLabelLong(isoDay: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay.trim());
  if (!m) return isoDay;
  const y = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isFinite(y) || month < 0 || month > 11 || day < 1 || day > 31) return isoDay;
  const dt = new Date(Date.UTC(y, month, day, 12, 0, 0));
  return `${WD_LONG[dt.getUTCDay()]} ${dayOrdinal(day)} ${MO_LONG[month]} ${y}`;
}

/** True for Saturday / Sunday on a `YYYY-MM-DD` reporting-day key (UTC noon parse). */
export function isReportingWeekendDay(isoDay: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay.trim());
  if (!m) return false;
  const y = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isFinite(y) || month < 0 || month > 11 || day < 1 || day > 31) return false;
  const dow = new Date(Date.UTC(y, month, day, 12, 0, 0)).getUTCDay();
  return dow === 0 || dow === 6;
}

/** Rotate compact day labels when a daily chart has more than this many bars. */
export const DENSE_DAILY_CHART_BAR_THRESHOLD = 12;

const WEEKEND_AXIS_FILL = "#ffffff";
const WEEKDAY_AXIS_FILL = "#9eb2d7";

/** Compact day axis text: `31/7 F`. */
export function dailyChartAxisLabel(isoDay: string): string {
  return formatDashboardDayLabelCompact(isoDay);
}

/** Weekend labels: bold white; weekdays: muted blue-grey. */
export function dailyChartAxisStyle(isoDay: string): {
  axisLabelFill: string;
  axisLabelFontWeight?: number;
} {
  if (isReportingWeekendDay(isoDay)) {
    return { axisLabelFill: WEEKEND_AXIS_FILL, axisLabelFontWeight: 700 };
  }
  return { axisLabelFill: WEEKDAY_AXIS_FILL };
}

export function dailyChartRotateAxisLabels(barCount: number): boolean {
  return barCount > DENSE_DAILY_CHART_BAR_THRESHOLD;
}

/** Fields to spread onto a daily BarChart item for reporting-day axes. */
export function dailyChartBarAxisProps(isoDay: string): {
  label: string;
  axisLabelFill: string;
  axisLabelFontWeight?: number;
} {
  return {
    label: dailyChartAxisLabel(isoDay),
    ...dailyChartAxisStyle(isoDay),
  };
}

/** Friendly filter range for export headers. */
export function formatFilterRangeLabel(
  from: string,
  to: string,
  timeZone: string,
): string {
  const fmt = (v: string, endOfDay: boolean) => {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return formatEventTimeLocal(d, timeZone);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const suffix = endOfDay ? " 23:59" : " 00:00";
      return `${formatDashboardDayLabel(v)}${suffix}`;
    }
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : formatEventTimeLocal(d, timeZone);
  };
  return `${fmt(from, false)} → ${fmt(to, true)} (local)`;
}

/**
 * JPN-style range start tag for exports: `_YYMMDD` in reporting timezone.
 * Example: 2026-07-08 → `_260708`.
 */
export function formatJpnRangeStartSuffix(value: string, timeZone: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, y, m, d] = value.match(/^(\d{4})-(\d{2})-(\d{2})$/) ?? [];
    if (!y || !m || !d) return "";
    return `_${y.slice(2)}${m}${d}`;
  }
  const d = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const p = calendarParts(d, timeZone);
  return `_${pad2(p.year % 100)}${pad2(p.monthIdx + 1)}${pad2(p.day)}`;
}

function reportingDayKeyFromEpoch(epochMs: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date(epochMs));
}

/**
 * Inclusive reporting-calendar days in the dashboard filter range (matches API `enumerateReportingDays`).
 */
export function reportingCalendarDayCount(from: string, to: string, timeZone: string): number {
  const fromNorm = normalizeFilterDateTime(from, from, "from");
  const toNorm = normalizeFilterDateTime(to, to, "to");
  const fromMs = new Date(fromNorm).getTime();
  const toMs = new Date(toNorm).getTime();
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return 1;

  const startKey = reportingDayKeyFromEpoch(fromMs, timeZone);
  const endKey = reportingDayKeyFromEpoch(toMs, timeZone);
  let [y, m, d] = startKey.split("-").map(Number);
  const [ey, em, ed] = endKey.split("-").map(Number);
  const endCompare = ey! * 10_000 + em! * 100 + ed!;
  let count = 0;
  while (true) {
    count += 1;
    const cmp = y! * 10_000 + m! * 100 + d!;
    if (cmp >= endCompare) break;
    const next = new Date(Date.UTC(y!, m! - 1, d! + 1));
    y = next.getUTCFullYear();
    m = next.getUTCMonth() + 1;
    d = next.getUTCDate();
  }
  return Math.max(1, count);
}

/** Average per reporting day, e.g. `12 /day` or `8.6 /day`. */
export function formatAvgPerDay(total: number, dayCount: number): string {
  const avg = total / Math.max(1, dayCount);
  const rounded =
    Math.abs(avg - Math.round(avg)) < 0.05 ? String(Math.round(avg)) : avg.toFixed(1);
  return `${rounded} /day`;
}
